import React, { useContext, useEffect, useMemo, useState } from 'react';

import { CssBaseline, LinearProgress, ThemeProvider } from '@material-ui/core';
import { Theme } from '@material-ui/core/styles';
import clsx from 'clsx';
import { useHistory, useLocation } from 'react-router-dom';

import { clearLSExcept, getFromLS, saveToLS } from '@vyce/core/src/utils/local-storage';
import { ChatUser, ColorThemeType, ThemeType } from '@vyce/core/src/types';
import { AppSnackbarProvider, Header, MainNotifications, Sidebar } from '@vyce/core/src/components';
import {
  ColorThemeProvider,
  DeviceContext,
  PermissionProvider,
  UnreadMessagesProvider,
} from '@vyce/core/src/contexts';
import { isMobileDevice, isValueExistsInEnum } from '@vyce/core/src/utils';
import { MAIN_CONTAINER_ID } from '@vyce/core/src/constants';
import AIChatContextProvider from '@vyce/core/src/views/ai/context';
import { LailaLauncher, LAILA_PAGE } from '@vyce/core/src/views/ai/components/launcher/LailaLauncher';
import { getTeamsAndLocationsOptionsRequest } from '@vyce/core/src/api/dashboards';
import useChatRequests from '@vyce/core/src/hooks/useChatRequests';
import { useFirebaseSession } from '@vyce/core/src/hooks/useFirebaseSession';
import { useUnreadMessagesCount } from '@vyce/core/src/hooks/useUnreadMessagesCount';
import { ensureUserExistsRequest } from '@vyce/core/src/api/chat';
import { isEffectiveAdminPosition } from '@vyce/core/src/utils/permissions';

import { AccountMenu } from 'src/views/main/components/AccountMenu';
import createRoutes from 'src/router';
import { useActions, useTypedSelector } from 'src/hooks';
import { InvitesContext } from 'src/store/invites-context';
import { store } from 'src/store/store';
import { isPhoneVerificationPending } from 'src/store/action-creators/user';
import { MainAppTour } from './components/MainAppTour';
import { DownloadAppDialog } from './components/DownloadAppDialog';
import { ActiveInvitesDialog } from './components/ActiveInvitesDialog';
import useStyles from './styles';
import { ROUTES_WITHOUT_NAVIGATION } from './constants';

const routes = createRoutes();
const MESSAGES_LINK = '/messages';
const VYCE_CHAT_COMPANY_ID = 'vyce';

interface Props {
  changeColorTheme: (type: ThemeType) => void;
  themeType?: ThemeType;
  colorTheme: ColorThemeType;
  theme: Theme;
}

export const Main: React.FC<Props> = ({ changeColorTheme, themeType, colorTheme, theme }) => {
  const classes = useStyles();
  const [loading, setLoading] = useState<boolean>(true);
  const [open, setOpen] = useState<boolean>(true);
  const [mobileOpen, setMobileOpen] = useState<boolean>(false);
  const token = getFromLS('token');

  const {
    clearRedirectTo,
    me,
    clearUser,
    clearHelpers,
    setShowTour,
    verifyPhone,
    setPosition,
    fetchCompanyData,
    updateMainDashboardFilters,
  } = useActions();
  const { user, helper } = useTypedSelector(state => state);
  // Global Firebase sign-in: every Firestore/callable/webhook request from here
  // on carries this user's ID token, ahead of backend auth enforcement.
  useFirebaseSession({ userId: user.uuid, email: user.email, phone: user.phone });
  const { isDesktop } = useContext(DeviceContext);
  const { activeInvites, activeInvitesDialogOpen, setActiveInvitesDialogOpen, redeemActiveAdminInvites } =
    useContext(InvitesContext);
  const [downloadAppDialogOpen, setDownloadAppDialogOpen] = useState(false);
  const history = useHistory();
  const { pathname } = useLocation();
  const { selectedCompanyAppData, showTour } = helper;
  const chatUser: ChatUser = {
    uid: user.uuid,
    name: `${user.first_name} ${user.last_name}`,
    email: user.email,
    phone: user.phone,
    avatarUrl: user.photo?.url,
  };
  const { syncAdminChats } = useChatRequests(chatUser);
  const isAuth = !!user?.first_name;
  const isUserVerified = user.email_verified || user.phone_verified;

  const { redirectTo, navItems, selectedPosition, selectedCompany } = helper;

  // Not `permissions.length` — that reads an owner (empty array by design) and a
  // purely location-scoped manager (rights in `resources`) as having nothing, so
  // their company data was never fetched at all.
  const hasPermissions = useMemo(() => isEffectiveAdminPosition(selectedPosition), [selectedPosition]);

  // The single source of truth about rights for every screen below, core
  // included. Company switching flows through here: setPosition dispatches
  // SET_SELECTED_POSITION, this memo changes identity, consumers re-evaluate.
  const permissionValue = useMemo(
    () => ({
      position: selectedPosition,
      isInternalAdmin: false,
      ready: !!user.uuid && !loading,
    }),
    [selectedPosition, user.uuid, loading]
  );

  // Unread badge on the Messages nav item. Mirrors EmployerMessages: without a messaging
  // subscription only the Vyce Notifications chat is shown, so only it is counted.
  const hasMessagesNavItem = useMemo(() => navItems.some(item => item.link === MESSAGES_LINK), [navItems]);
  const unreadCompanyIds = useMemo(
    () =>
      selectedCompanyAppData?.messagingSubscription && selectedCompany?.uuid
        ? [VYCE_CHAT_COMPANY_ID, selectedCompany.uuid]
        : [VYCE_CHAT_COMPANY_ID],
    [selectedCompanyAppData?.messagingSubscription, selectedCompany?.uuid]
  );
  const unreadMessages = useUnreadMessagesCount({
    userId: user.uuid,
    companyIds: unreadCompanyIds,
    enabled: !!selectedCompany?.uuid && hasMessagesNavItem,
  });
  const navItemsWithCounts = useMemo(
    () =>
      navItems.map(item =>
        item.link === MESSAGES_LINK ? { ...item, count: unreadMessages.totalUnreadCount } : item
      ),
    [navItems, unreadMessages.totalUnreadCount]
  );

  const withoutNavigation = useMemo(
    () => isValueExistsInEnum(pathname, ROUTES_WITHOUT_NAVIGATION),
    [pathname]
  );

  const showHeader = useMemo(() => isAuth && !withoutNavigation, [isAuth, withoutNavigation]);

  const showSidebar = useMemo(
    () => !loading && user.uuid && !withoutNavigation,
    [loading, user.uuid, withoutNavigation]
  );

  const handleDrawerToggle = () => {
    if (isDesktop) {
      return setOpen(!open);
    }
    setMobileOpen(!mobileOpen);
  };

  const logout = () => {
    setOpen(false);
    setMobileOpen(false);
    clearHelpers();
    clearUser();
    // generalDashboardWidgetsState is keyed by user uuid, so it is safe to keep
    // across accounts (see views/dashboards/employer/EmployerDashboard.tsx).
    setTimeout(() =>
      clearLSExcept(['teamDashboardItems', 'teamDashboardLayouts', 'generalDashboardWidgetsState'])
    );
  };

  const fetchTeamsAndLocationsOptions = async (companyId: string) => {
    try {
      const res = await getTeamsAndLocationsOptionsRequest(companyId);
      updateMainDashboardFilters({
        locations: res.data.locations,
        teams: res.data.teams,
      });
    } catch (e) {
      console.error(e);
    }
  };

  const ensureFirebaseUserExists = async (firebaseUser: ChatUser) => {
    try {
      await ensureUserExistsRequest(firebaseUser);
    } catch (e) {
      console.error(e);
    }
  };

  const fetchUserData = async () => {
    await me();
    if (!isPhoneVerificationPending(store.getState().user)) {
      await redeemActiveAdminInvites();
    }
    setLoading(false);
  };

  useEffect(() => {
    if (token) {
      setLoading(true);
      fetchUserData();
    } else {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (selectedCompany?.uuid && hasPermissions) {
      fetchCompanyData(selectedCompany.uuid);
      fetchTeamsAndLocationsOptions(selectedCompany.uuid);
    }
  }, [selectedCompany?.uuid, hasPermissions]);

  useEffect(() => {
    if (selectedCompanyAppData?.subscription?.active && selectedPosition) {
      const chatUser: ChatUser = {
        email: user.email,
        phone: user.phone,
        name: `${user.first_name} ${user.last_name}`,
        uid: user.uuid,
        avatarUrl: user?.photo?.url,
      };
      syncAdminChats(selectedCompanyAppData, selectedPosition, chatUser);
    }
  }, [selectedCompanyAppData?.subscription?.active, selectedPosition]);

  // listens and redirects from state
  useEffect(() => {
    if (redirectTo) {
      history.push(redirectTo);
      clearRedirectTo();
    }
  }, [clearRedirectTo, redirectTo, history]);

  // Keep the Firebase user (and the denormalised profiles in chats) in sync whenever
  // the user edits their name / email / phone / avatar — not just on first login.
  useEffect(() => {
    if (chatUser.uid && chatUser.uid !== 'vyce') {
      ensureFirebaseUserExists(chatUser);
    }
  }, [chatUser.uid, chatUser.name, chatUser.email, chatUser.phone, chatUser.avatarUrl]);

  useEffect(() => {
    const DOWNLOAD_APP_DIALOG_SHOWN_KEY = 'downloadAppDialogShown';
    if (
      !loading &&
      user.uuid &&
      user.isCompanyAdmin &&
      isMobileDevice() &&
      !getFromLS(DOWNLOAD_APP_DIALOG_SHOWN_KEY) &&
      !showTour &&
      isUserVerified
    ) {
      saveToLS(DOWNLOAD_APP_DIALOG_SHOWN_KEY, true);
      setDownloadAppDialogOpen(true);
    }
  }, [loading, user.uuid, user.isCompanyAdmin, showTour, isUserVerified]);

  return (
    <ColorThemeProvider value={{ colorTheme }}>
      <ThemeProvider theme={theme}>
        <AppSnackbarProvider>
          <PermissionProvider value={permissionValue}>
            <UnreadMessagesProvider value={unreadMessages}>
              <div className={classes.root}>
                <CssBaseline />
                {showHeader && (
                  <Header
                    themeType={themeType}
                    loading={loading}
                    handleDrawerToggle={handleDrawerToggle}
                    changeColorTheme={changeColorTheme}
                    open={open}
                    user={user}
                    setShowTour={setShowTour}
                  >
                    <AccountMenu user={user} selectedPosition={selectedPosition} setPosition={setPosition} />
                  </Header>
                )}

                {showSidebar && (
                  <>
                    <Sidebar
                      handleDrawerToggle={handleDrawerToggle}
                      open={open}
                      logout={logout}
                      mobileOpen={mobileOpen}
                      setMobileOpen={setMobileOpen}
                      navItems={navItemsWithCounts}
                    />
                  </>
                )}
                {/* Laila's conversation outlives the /vyce-ai page; a new user or company starts a fresh one.
                    She knows the Vyce help articles for the user's role, so she also answers "how do I…". */}
                <AIChatContextProvider
                  key={`${user.uuid}:${selectedCompany?.uuid}`}
                  user={user}
                  companyId={selectedCompany?.uuid}
                  faq={user.isCompanyAdmin ? 'employer' : 'worker'}
                >
                  <main
                    id={MAIN_CONTAINER_ID}
                    className={clsx(classes.content, {
                      [classes.contentShift]: open,
                      [classes.authLayout]: !isAuth,
                      [classes.withNavigation]: !withoutNavigation,
                    })}
                  >
                    {loading ? <LinearProgress className={classes.progress} /> : <>{routes}</>}
                  </main>

                  {/* Laila needs a signed-in user: her API rejects requests without a Vyce token. */}
                  {isAuth && <LailaLauncher hidden={pathname.startsWith(LAILA_PAGE)} />}
                </AIChatContextProvider>

                {showTour && <MainAppTour isEmployer={!!user.isCompanyAdmin} />}

                <MainNotifications userId={user.uuid} verifyPhone={verifyPhone} />

                <DownloadAppDialog
                  open={downloadAppDialogOpen}
                  onClose={() => setDownloadAppDialogOpen(false)}
                />

                <ActiveInvitesDialog
                  open={activeInvitesDialogOpen}
                  invites={activeInvites}
                  onClose={() => setActiveInvitesDialogOpen(false)}
                />
              </div>
            </UnreadMessagesProvider>
          </PermissionProvider>
        </AppSnackbarProvider>
      </ThemeProvider>
    </ColorThemeProvider>
  );
};
